import { ChevronDown, Menu, Phone, X } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { assets, siteConfig } from "@/data/site";
import { headerNavigation } from "@/data/navigation";

type NavigationLinkProps = {
  href: string;
  className?: string;
  tabIndex?: number;
  onClick?: () => void;
  children: ReactNode;
};

type MenuKey = "partitions";

const headerMenus = [headerNavigation.partitions] as const;

function NavigationLink({ href, className, tabIndex, onClick, children }: NavigationLinkProps) {
  if (href.includes("#")) {
    return <a href={href} className={className} tabIndex={tabIndex} onClick={onClick}>{children}</a>;
  }

  return <Link href={href} className={className} tabIndex={tabIndex} onClick={onClick}>{children}</Link>;
}

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [desktopMenuOpen, setDesktopMenuOpen] = useState<MenuKey | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<MenuKey | null>(null);
  const [location] = useLocation();
  const desktopNavRef = useRef<HTMLElement>(null);
  const menuToggleRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setOpen(false);
    setDesktopMenuOpen(null);
    const activeMenu = headerMenus.find((menu) => location.startsWith(menu.href));
    setMobileMenuOpen(activeMenu?.key ?? null);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        setMobileMenuOpen(null);
        menuToggleRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  useEffect(() => {
    if (!desktopMenuOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!desktopNavRef.current?.contains(event.target as Node)) setDesktopMenuOpen(null);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setDesktopMenuOpen(null);
        desktopNavRef.current?.querySelector<HTMLButtonElement>('button[aria-expanded="true"]')?.focus();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [desktopMenuOpen]);

  const isActive = (href: string) => {
    const [path, hash] = href.split("#");
    if (hash) return false;
    return location === path || location.startsWith(`${path}/`);
  };

  return (
    <header className="site-header site-header--simple">
      <div className="site-header__utility">
        <p>Commercial bathroom partitions & accessories · Nationwide shipping</p>
        <a href={`mailto:${siteConfig.email}`}>{siteConfig.email}</a>
      </div>
      <div className="site-header__main">
        <Link href="/" className="site-logo" aria-label="Partition Stalls home">
          <img src={assets.logo} alt="Partition Stalls" width="320" height="108" />
        </Link>
        <nav ref={desktopNavRef} className="desktop-nav desktop-nav--simple" aria-label="Primary navigation">
          {headerMenus.map((menu) => {
            const menuOpen = desktopMenuOpen === menu.key;
            const menuActive = location.startsWith(menu.href);
            const submenuId = `${menu.key}-submenu`;

            return (
              <div
                key={menu.key}
                className={`desktop-nav__dropdown ${menuOpen ? "is-open" : ""}`}
                onMouseEnter={() => setDesktopMenuOpen(menu.key)}
                onMouseLeave={() => setDesktopMenuOpen(null)}
                onFocusCapture={() => setDesktopMenuOpen(menu.key)}
                onBlurCapture={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDesktopMenuOpen(null);
                }}
              >
                <NavigationLink
                  href={menu.href}
                  className={`desktop-nav__menu-link ${menuActive ? "is-active" : ""}`}
                  onClick={() => setDesktopMenuOpen(null)}
                >
                  {menu.label}
                </NavigationLink>
                <button
                  type="button"
                  className="desktop-nav__menu-toggle"
                  aria-expanded={menuOpen}
                  aria-controls={submenuId}
                  aria-label={`Open ${menu.label} menu`}
                  onClick={() => setDesktopMenuOpen((current) => current === menu.key ? null : menu.key)}
                >
                  <ChevronDown aria-hidden="true" />
                </button>
                <div id={submenuId} className="desktop-nav__submenu">
                  {menu.children.map((item, index) => (
                    <NavigationLink key={item.href} href={item.href} onClick={() => setDesktopMenuOpen(null)}>
                      <span>0{index + 1}</span>
                      <strong>{item.label}</strong>
                    </NavigationLink>
                  ))}
                </div>
              </div>
            );
          })}
          <NavigationLink href={headerNavigation.accessories.href} className={`desktop-nav__direct ${isActive(headerNavigation.accessories.href) ? "is-active" : ""}`}>
            {headerNavigation.accessories.label}
          </NavigationLink>
          {headerNavigation.direct.map((item) => (
            <NavigationLink key={item.href} href={item.href} className={`desktop-nav__direct ${isActive(item.href) ? "is-active" : ""}`}>
              {item.label}
            </NavigationLink>
          ))}
        </nav>
        <div className="site-header__actions">
          <a href={`tel:${siteConfig.phoneHref}`} className="header-phone" aria-label={`Call ${siteConfig.phoneDisplay}`}>
            <Phone aria-hidden="true" />
            <span>{siteConfig.phoneDisplay}</span>
          </a>
          <Link href="/request-a-quote" className="button button--primary header-quote">Get a Free Quote.</Link>
          <button ref={menuToggleRef} type="button" className="menu-toggle" aria-label={open ? "Close navigation" : "Open navigation"} aria-expanded={open} aria-controls="mobile-navigation" onClick={() => setOpen((current) => !current)}>
            {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </button>
        </div>
      </div>
      <div id="mobile-navigation" className={`mobile-nav mobile-nav--simple ${open ? "is-open" : ""}`} aria-hidden={!open}>
        <nav aria-label="Mobile navigation">
          {headerMenus.map((menu, menuIndex) => {
            const menuOpen = mobileMenuOpen === menu.key;
            const submenuId = `mobile-${menu.key}-submenu`;

            return (
              <div className={`mobile-nav__menu ${menuOpen ? "is-open" : ""}`} key={menu.key}>
                <div className="mobile-nav__menu-header">
                  <NavigationLink
                    href={menu.href}
                    className="mobile-nav__menu-link"
                    tabIndex={open ? 0 : -1}
                    onClick={() => setOpen(false)}
                  >
                    <span>0{menuIndex + 1}</span>
                    <strong>{menu.label}</strong>
                  </NavigationLink>
                  <button
                    type="button"
                    className="mobile-nav__menu-trigger"
                    aria-expanded={menuOpen}
                    aria-controls={submenuId}
                    aria-label={`Open ${menu.label} menu`}
                    tabIndex={open ? 0 : -1}
                    onClick={() => setMobileMenuOpen((current) => current === menu.key ? null : menu.key)}
                  >
                    <ChevronDown aria-hidden="true" />
                  </button>
                </div>
                <div id={submenuId} className="mobile-nav__menu-links" hidden={!menuOpen}>
                  {menu.children.map((item, index) => (
                    <NavigationLink key={item.href} href={item.href} tabIndex={open && menuOpen ? 0 : -1} onClick={() => setOpen(false)}>
                      <span>0{index + 1}</span>
                      <strong>{item.label}</strong>
                    </NavigationLink>
                  ))}
                </div>
              </div>
            );
          })}
          <NavigationLink
            href={headerNavigation.accessories.href}
            className="mobile-nav__menu-link mobile-nav__menu-link--direct"
            tabIndex={open ? 0 : -1}
            onClick={() => setOpen(false)}
          >
            <span>02</span>
            <strong>{headerNavigation.accessories.label}</strong>
          </NavigationLink>
          {headerNavigation.direct.map((item, index) => (
            <NavigationLink key={item.href} href={item.href} tabIndex={open ? 0 : -1} onClick={() => setOpen(false)}>
              <span>0{index + headerMenus.length + 2}</span>
              <strong>{item.label}</strong>
            </NavigationLink>
          ))}
          <Link href="/request-a-quote" className="mobile-nav__quote" tabIndex={open ? 0 : -1} onClick={() => setOpen(false)}><span>06</span><strong>Get a Free Quote.</strong></Link>
        </nav>
        <div className="mobile-nav__contact">
          <a href={`tel:${siteConfig.phoneHref}`} tabIndex={open ? 0 : -1}>{siteConfig.phoneDisplay}</a>
          <a href={`mailto:${siteConfig.email}`} tabIndex={open ? 0 : -1}>{siteConfig.email}</a>
        </div>
      </div>
    </header>
  );
}
